"use client"

import { productsLeadsIcons } from "@/public/icons/ProductsLeadIcons"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import Image from "next/image"
import { useInView } from "react-intersection-observer"
import { transitionDelayTime, useInViewConfig, useTransitionUpStyles } from "ui"
import ButtonWithArrow, {
  colorPalletteIndexType,
} from "ui/common/bit/ButtonWithArrow"
import { mediaQueries } from "ui/theme/mediaQueries"
import { colors, sizes } from "ui/theme/theme"
import { extractMenuProperties, useUtmUrl } from "utils"

import { sendBlockEvent } from "utils/gtm"
import EnhancedTypography from "../common/bit/EnhancedTypography"

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRedBorder",
})<{ isRedBorder: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: fit-content;
  background: rgba(255, 255, 255, 0.95);
  border-radius: ${sizes.borderRadius};
  border: 1px solid
    ${(props) => (props.isRedBorder ? colors.red : "transparent")};
  gap: 20px;
  padding: 20px 28px 20px 20px;
  box-shadow: -4px 8px 30px 0px rgba(2, 1, 64, 0.12);
  z-index: 5;
  margin-top: ${(props) => (props.isRedBorder ? "-55px" : "-100px")};
  h4 {
    color: ${colors.red};
    &:last-of-type {
      color: ${colors.black};
    }
  }
  ${mediaQueries.mobile} {
    flex-direction: column;
    width: 380px;
    gap: 12px;
    padding: ${(props) => (props.isRedBorder ? "20px 30px" : "32px 24px")};
    margin-bottom: ${(props) => (props.isRedBorder ? "-105px" : "-405px")};
    margin-top: -60px;
  }
  ${mediaQueries.tablet} {
    flex-direction: column;
    padding: ${(props) => (props.isRedBorder ? "20px 30px" : "32px 24px")};
    gap: 12px;
    margin-bottom: ${(props) => (props.isRedBorder ? "-115px" : "-420px")};
    margin-top: -60px;
  }

  ${mediaQueries.desktopM} {
    flex-direction: row;
    width: 1166px;
    max-width: 100%;
    padding: ${(props) => (props.isRedBorder ? "20px" : "20px 28px 20px 20px")};
    gap: 12px;
    margin-bottom: ${(props) => (props.isRedBorder ? "0" : "46px")};
    margin-top: -100px;
  }
  ${mediaQueries.desktopXl} {
    width: 1560px;
  }
`
const StyledStripLeadProductsTitle = styled(EnhancedTypography)`
  margin-inline-end: 20px;
  width: 100%;
  line-height: 120%;

  ${mediaQueries.mobile} {
    width: auto;
    text-align: center;
    margin-inline-end: 0px;
    margin-bottom: 4px;
  }
  ${mediaQueries.tablet} {
    width: auto;
    text-align: center;
    margin-inline-end: 0px;
    margin-bottom: 4px;
  }

  ${mediaQueries.desktopM} {
    margin-inline-end: 0px;
    text-align: start;
  }

  ${mediaQueries.desktop} {
    margin-inline-end: 20px;
    width: fit-content;
    text-align: start;
  }
`
const StyledStripLeadProductContainer = styled(ButtonWithArrow)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-radius: ${sizes.borderRadius};
  background: ${colors.white};
  padding: 20px 16px;
  width: 100%;
  ${mediaQueries.desktopM} {
    width: 230px;
    height: 76px;
    padding: 12px;
  }
  ${mediaQueries.desktopXl} {
    width: 300px;
    height: 90px;
  }
`
const StyledButtonWithArrow = styled(ButtonWithArrow)`
  margin-inline-start: auto;
  margin-inline-end: auto;
  ${mediaQueries.desktopM} {
    margin-inline-end: unset;
  }
`
const StyledStripLeadProductTitle = styled(Typography)<{ component?: string }>`
  text-align: start;
`
const StyledStripLeadProductPrice = styled(Typography)`
  text-align: start;
`

const StripLeadProducts = ({ data, id }: { data: any; id?: string }) => {
  const transformedData = data
  const { ref, inView } = useInView(useInViewConfig)
  const divransitionStyles = useTransitionUpStyles(inView, transitionDelayTime)
  const { title, text, button } = transformedData
  const isRedBorder = transformedData?.flagshipProductsItems?.blocks
    ? false
    : true

  // Use the custom hook for UTM URL handling
  const getUrlWithUtm = useUtmUrl()
  return (
    <StyledContainer
      isRedBorder={isRedBorder}
      id={id}
      style={{ ...divransitionStyles }}
      ref={ref}
    >
      {title?.value && (
        <StyledStripLeadProductsTitle
          component="p"
          variant="h4"
          color={text?.value ? colors.red : colors.black}
        >
          {title.value}
        </StyledStripLeadProductsTitle>
      )}
      {text?.value && (
        <StyledStripLeadProductsTitle component="p" variant="h4">
          {text.value}
        </StyledStripLeadProductsTitle>
      )}
      {transformedData?.flagshipProductsItems?.blocks.map(
        (product: any, i: number) => {
          const icon = extractMenuProperties(product).icon
          const button = extractMenuProperties(product).button[0]
          const subtitle = extractMenuProperties(product).title
          const primaryColor = extractMenuProperties(product).primaryColor
          // GTM event handler
          const handleClick = () => {
            sendBlockEvent({
              blockId: id || "1",
              componentName: "סטריפ באנר",
              title: transformedData?.title?.value,
              productName: data?.formDefaultInsuranceType,
              actionName: undefined,
              buttonName: button?.name,
            })
          }
          return (
            <StyledStripLeadProductContainer
              key={`flagshipProductsItems-${i}`}
              href={getUrlWithUtm(button?.url) || ""}
              variant="elevated"
              arrowName={productsLeadsIcons[i].arrow || "arrow_lightPink_black"}
              colorPallette={
                (productsLeadsIcons[i].color as colorPalletteIndexType) || "red"
              }
              onClick={handleClick}
            >
              {icon && (
                <Image
                  src={icon}
                  alt={`${button?.name || ""}`}
                  width={40}
                  height={40}
                />
              )}
              <Box>
                <StyledStripLeadProductTitle variant="h5" component="h3">
                  {button?.name}
                </StyledStripLeadProductTitle>
                <StyledStripLeadProductPrice variant="body1">
                  {subtitle}
                </StyledStripLeadProductPrice>
              </Box>
            </StyledStripLeadProductContainer>
          )
        },
      )}
      {button?.links[0] && (
        <StyledButtonWithArrow
          href={getUrlWithUtm(button?.links[0]?.url?.split("?utm_")[0])}
          onClick={() => {
            sendBlockEvent({
              blockId: id || "1",
              componentName: "סטריפ באנר",
              title: transformedData?.title?.value,
              productName: undefined,
              actionName: undefined,
              buttonName: button.links[0]?.name,
            })
          }}
        >
          {button.links[0]?.name}
        </StyledButtonWithArrow>
      )}
    </StyledContainer>
  )
}

export default StripLeadProducts
